/**
 * Candidate pairs for the synthesis scan, chosen by the shape of the graph.
 *
 * The scan used to hand the model its forty-four highest nodes by novelty times
 * confidence and ask it to nominate pairs — so the pool a synthesis could be
 * drawn from was, before the model was even asked, the model's own opinion of
 * its own nodes, and a pair it liked was a pair it had liked twice. AGENTS.md
 * invariant 10 took the score out of the ORDER of candidates; this module takes
 * the self-rating out of their SELECTION. Nothing here reads a confidence, a
 * novelty, a tension or a score, and the test beside it reads this file to make
 * sure of it.
 *
 * Two numbers, both re-derivable from the graph by anyone holding the document:
 *
 *   distance        Tree edges between the two nodes through their lowest common
 *                   ancestor, over `parentId` only. A parent and child are one
 *                   edge apart, siblings two, and a synthesis wants ends that sit
 *                   on different branches — the further apart, the less the two
 *                   claims already share by descent.
 *   dissimilarity   One minus the Jaccard overlap of the two claims' distinctive
 *                   terms, split by the corpus retriever's own `tokenise` so a
 *                   word means the same thing here as it does there. Distance
 *                   says the two are far apart in the argument; this says they
 *                   are far apart in what they are ABOUT. Two branches that each
 *                   restate the seed in their own words are far by the first
 *                   measure and nothing by the second.
 *
 * Both ride out on `Bridge.basis`, so the card can say why a pair was put in
 * front of the model at all; `basisOf` computes the same two numbers for a pair
 * the model nominated on its own, so the two kinds of candidate can be read
 * side by side. Neither number is a verdict. A pair chosen here is a pair worth
 * ASKING about, and the model is still the one that says whether the two fuse
 * — what changed is that it is no longer also the one that chose the question.
 *
 * Pure, and a LEAF: runtime imports from `./types` and `./retrieve` and nothing
 * else, so the engine, the store and the UI can all read it without a cycle.
 */

import { pairKey, type Bridge, type BridgeBasis, type ReasonNode, type Session } from './types'
import { tokenise } from './retrieve'

export interface PairCandidate {
  /** The two node ids, in `pairKey` order — sorted, so A × B and B × A are one row. */
  a: string
  b: string
  distance: number
  dissimilarity: number
}

/**
 * Fewer edges than this and the two ends are one line of thought.
 *
 * One edge is a parent and its child — a continuation, which the scan prompt
 * has always refused. Two is a pair of siblings, or a grandparent and its
 * grandchild: two answers to one question, or a claim and what follows from
 * what follows from it, and fusing either is the parent's own synthesis said
 * again. Three is the first distance at which the two ends sit under different
 * branches of a shared ancestor with a claim of their own between them and it.
 */
export const PAIR_MIN_DISTANCE = 3

/**
 * Less lexical daylight than this and the two ends are one claim in two places.
 *
 * A Jaccard overlap above one half means the terms the two claims share
 * outnumber the terms either has alone — a restatement, or two branches that
 * each arrived at the same sentence. There is nothing to fuse in that: a
 * synthesis is something neither end says alone, and a pair that already says
 * the same thing leaves the model nothing to add but wording.
 */
export const PAIR_MIN_DISSIMILARITY = 0.5

/**
 * How many pairs a scan is handed when it does not say. Eight is twice the four
 * candidates the prompt asks the model to return, so the model still chooses,
 * and small enough that every pair on the list is one the graph's shape put
 * there rather than the tail of a ranking.
 */
export const PAIR_MAX = 8

const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

/* ── the tree ──────────────────────────────────────────────────────────── */

/**
 * The tree as integer arrays: every node numbered, its parent's number, and
 * its depth, all over `parentId` alone. Built once per call and never kept —
 * the store replaces `nodes` on every change, and a memo keyed on the old
 * object would answer for a graph that is no longer there. Integers rather
 * than ids because the pairing below walks this forty-five thousand times for
 * three hundred nodes, and a string lookup per step made the first version of
 * it ten times slower than the arithmetic.
 */
interface TreeIndex {
  at: Map<string, number>
  /** −1 for a root — and for a parent that is not in the graph, which makes the
   *  node the root of a tree of its own. A pair across two trees has no path,
   *  and `distanceOn` reports that as `Infinity` rather than as a guess. */
  parent: Int32Array
  depth: Int32Array
}

function indexTree(nodes: Record<string, ReasonNode>): TreeIndex {
  const ids = Object.keys(nodes)
  const at = new Map(ids.map((id, i) => [id, i] as const))
  const parent = new Int32Array(ids.length)
  ids.forEach((id, i) => {
    const p = nodes[id]!.parentId
    parent[i] = p !== null && at.has(p) ? at.get(p)! : -1
  })
  const depth = new Int32Array(ids.length).fill(-1)
  const path: number[] = []
  for (let i = 0; i < ids.length; i++) {
    if (depth[i] >= 0) continue
    // Walk up until a node whose depth is known or a root, then hand depths
    // back down the path. Each node is walked once, so the index is O(n). The
    // bound is a cycle guard: a chain longer than the graph is a document that
    // points at itself, and it is given a depth rather than a hang.
    path.length = 0
    let cur = i
    while (cur >= 0 && depth[cur] < 0 && path.length <= ids.length) {
      path.push(cur)
      cur = parent[cur]
    }
    let d = cur >= 0 && depth[cur] >= 0 ? depth[cur] : -1
    for (let k = path.length - 1; k >= 0; k--) depth[path[k]!] = ++d
  }
  return { at, parent, depth }
}

/**
 * Edges between two numbered nodes through their lowest common ancestor.
 *
 * Climb the deeper one to the shallower one's depth, then climb both together
 * until they meet. O(depth), no allocation, and the number of steps taken is
 * the number of edges — there is no second arithmetic to reconcile it against.
 * Honest depths put the meeting within `depth` steps of the levelling; the
 * bound is what makes a document whose parents form a cycle answer "no path"
 * rather than circle it, because two nodes on one cycle can climb for ever
 * without ever being the same node.
 */
function distanceOn(t: TreeIndex, a: number, b: number): number {
  if (a === b) return 0
  let da = t.depth[a]
  let db = t.depth[b]
  let x = a
  let y = b
  let steps = 0
  while (da > db && x >= 0) {
    x = t.parent[x]
    da--
    steps++
  }
  while (db > da && y >= 0) {
    y = t.parent[y]
    db--
    steps++
  }
  let left = da
  while (x >= 0 && y >= 0 && x !== y && left-- > 0) {
    x = t.parent[x]
    y = t.parent[y]
    steps += 2
  }
  // Climbed off the top, or out of steps, without meeting: no path at all.
  return x >= 0 && x === y ? steps : Infinity
}

/**
 * Tree edges between two nodes through their lowest common ancestor, counted
 * over `parentId` only — `bridgeFrom`, `dependsOn` and `attacks` are not the
 * tree, and a synthesis that already joins two branches must not make them
 * neighbours for the next scan. Zero for a node and itself; `Infinity` when
 * either is not in the graph or the two share no ancestor, which is the honest
 * arithmetic for "no path" and is what `basisOf` and `candidatePairs` refuse to
 * store.
 */
export function treeDistance(s: Pick<Session, 'nodes'>, a: string, b: string): number {
  const t = indexTree(s.nodes)
  const ia = t.at.get(a)
  const ib = t.at.get(b)
  return ia === undefined || ib === undefined ? Infinity : distanceOn(t, ia, ib)
}

/* ── the words ─────────────────────────────────────────────────────────── */

const termsOf = (n: Pick<ReasonNode, 'label' | 'claim'>): Set<string> =>
  new Set(tokenise(`${n.label} ${n.claim}`))

/**
 * One minus the Jaccard overlap of two term sets, in [0, 1].
 *
 * A node with no distinctive term at all — a claim that is entirely stop words
 * — cannot be shown to differ from anything, and the ambiguous case lands on
 * the side that proposes nothing: zero, under the floor, rather than one, at
 * the top of the ranking.
 */
function dissimilarityOf(x: ReadonlySet<string>, y: ReadonlySet<string>): number {
  if (!x.size || !y.size) return 0
  const [small, large] = x.size <= y.size ? [x, y] : [y, x]
  let shared = 0
  for (const t of small) if (large.has(t)) shared++
  return 1 - shared / (x.size + y.size - shared)
}

/** How little two claims share in words: one minus the Jaccard overlap of the
 *  distinctive terms in `label` and `claim` together. 0 is the same words, 1 is
 *  no word in common. */
export function lexicalDissimilarity(
  a: Pick<ReasonNode, 'label' | 'claim'>,
  b: Pick<ReasonNode, 'label' | 'claim'>,
): number {
  return dissimilarityOf(termsOf(a), termsOf(b))
}

/* ── the candidates ────────────────────────────────────────────────────── */

/**
 * The two numbers for a pair the MODEL nominated, so its card can print the
 * same arithmetic a chosen pair prints. Not thresholded — a nominated pair one
 * edge apart is reported as one edge apart, which is the point of reporting
 * it. Null when either end is not on the graph, or when the two ends share no
 * ancestor: the codec keeps a basis only when both numbers are finite, and a
 * basis that could not be stored would be a basis the card shows once.
 */
export function basisOf(s: Pick<Session, 'nodes'>, a: string, b: string): BridgeBasis | null {
  const na = s.nodes[a]
  const nb = s.nodes[b]
  if (!na || !nb || a === b) return null
  const distance = treeDistance(s, a, b)
  if (!Number.isFinite(distance)) return null
  return { distance, dissimilarity: lexicalDissimilarity(na, nb) }
}

/**
 * `pairKey` for two local ends, by delegation rather than by restating the
 * join: a candidate is checked against `exclude` under the exact key a proposed
 * `Bridge` carries, because two spellings of one key is how a pair that was
 * already proposed comes to be proposed again. Only `a` and `b` are read.
 */
function localPairKey(a: string, b: string): string {
  return pairKey({ a: { where: 'local', nodeId: a }, b: { where: 'local', nodeId: b } } as Bridge)
}

/**
 * Whether a node can be one end of a pair.
 *
 * The seed is the idea under examination, and every pair would otherwise be
 * "the seed and something", which is not a synthesis but the graph. A
 * contradiction is an objection to its parent, not a claim of its own to fuse.
 * A terminal node has been reasoned to exhaustion — unless it carries a recorded
 * result, in which case it stays, for the reason the scan prompt already gives:
 * a measured dead end is often the best thing in the graph to fuse something
 * with, and the result is the one fact here the model did not write.
 */
const eligible = (n: ReasonNode): boolean =>
  n.parentId !== null &&
  n.kind !== 'seed' &&
  n.kind !== 'contradiction' &&
  (!n.terminal || n.outcome !== undefined)

/** A candidate with the two ages its place in the order is decided on. */
interface Ranked extends PairCandidate {
  /** When the pair came into being: the younger node's age. */
  born: number
  /** The older node's age, to split pairs born together. */
  elder: number
}

/**
 * Negative when `p` belongs above `q`: further first, then less alike, then the
 * older pair, then the older elder, then the ids — so the same graph always
 * answers with the same list, whatever order its nodes were walked in.
 */
const rank = (p: Ranked, q: Ranked): number =>
  q.distance - p.distance ||
  q.dissimilarity - p.dissimilarity ||
  p.born - q.born ||
  p.elder - q.elder ||
  cmp(p.a, q.a) ||
  cmp(p.b, q.b)

/**
 * The pairs the graph's shape puts forward, furthest and least alike first.
 *
 * Every unordered pair of eligible nodes has its distance measured — n² over
 * the pool, deliberately, because a pair the graph never measured is a pair it
 * can never say it chose against — and everything else is spent only where it
 * can matter. The list keeps the best `max` seen so far, and a pair whose
 * distance cannot beat the list's worst is never tokenised, keyed or
 * allocated; the distance is an integer walk over `TreeIndex`, so three
 * hundred nodes is forty-five thousand pairs and a few milliseconds.
 *
 * Dropped: pairs under either floor; a node and its own ancestor at any
 * distance, because a descendant already rests on the claim above it and
 * their fusion is the branch restating itself; pairs a synthesis on the graph
 * already fuses; and pairs in `exclude`, which is where the caller passes the
 * `pairKey` of every candidate already proposed. With `focusIds`, a pair must
 * touch the focus — one end in the set, the other anywhere — because a person
 * who has focused a subtree is asking what elsewhere fuses with what they are
 * looking at, and pairs inside a subtree are the ones its shape already makes
 * close.
 *
 * The order is distance, then dissimilarity, then the older pair first — a
 * pair is as old as the younger of its two nodes — and then the ids. Age is
 * read off `createdOrdinal` when every node in the pool has one and off
 * `createdAt` otherwise, never a mixture: an ordinal and a millisecond on one
 * axis is the trap AGENTS.md documents under `RunMove.at`.
 */
export function candidatePairs(
  s: Pick<Session, 'nodes' | 'order'>,
  opts: { max?: number; exclude?: Iterable<string>; focusIds?: Set<string> } = {},
): PairCandidate[] {
  const max = opts.max ?? PAIR_MAX
  if (max <= 0) return []
  const skip = new Set(opts.exclude ?? [])
  for (const n of Object.values(s.nodes)) {
    if (n.parentId === null || !n.bridgeFrom?.length) continue
    const ends = [n.parentId, ...n.bridgeFrom]
    for (let i = 0; i < ends.length; i++)
      for (let j = i + 1; j < ends.length; j++) skip.add(localPairKey(ends[i]!, ends[j]!))
  }

  const tree = indexTree(s.nodes)
  const pool = s.order.map((id) => s.nodes[id]).filter((n): n is ReasonNode => !!n && eligible(n))
  const byOrdinal = pool.every((n) => n.createdOrdinal !== undefined)
  const age = (n: ReasonNode): number => (byOrdinal ? n.createdOrdinal! : n.createdAt)
  pool.sort((p, q) => age(p) - age(q) || cmp(p.id, q.id))
  // Per pool position, once: the tree number, the age and the terms.
  const at = pool.map((n) => tree.at.get(n.id)!)
  const ages = pool.map(age)
  const terms = pool.map(termsOf)
  const focus = opts.focusIds
  const inFocus = focus ? pool.map((n) => focus.has(n.id)) : null

  // Best first, never longer than `max`; the last entry is the one to beat.
  const top: Ranked[] = []
  for (let i = 0; i < pool.length; i++) {
    for (let j = i + 1; j < pool.length; j++) {
      if (inFocus && !inFocus[i] && !inFocus[j]) continue
      const distance = distanceOn(tree, at[i]!, at[j]!)
      if (!Number.isFinite(distance) || distance < PAIR_MIN_DISTANCE) continue
      // Lineal: the shallower node is the other's ancestor exactly when the
      // distance is their difference in depth.
      if (distance === Math.abs(tree.depth[at[i]!] - tree.depth[at[j]!])) continue
      const worst = top.length >= max ? top[top.length - 1]! : null
      if (worst && distance < worst.distance) continue
      const dissimilarity = dissimilarityOf(terms[i]!, terms[j]!)
      if (dissimilarity < PAIR_MIN_DISSIMILARITY) continue
      const p = pool[i]!
      const q = pool[j]!
      const row: Ranked = {
        a: p.id < q.id ? p.id : q.id,
        b: p.id < q.id ? q.id : p.id,
        distance,
        dissimilarity,
        born: ages[j]!,
        elder: ages[i]!,
      }
      if (worst && rank(row, worst) >= 0) continue
      if (skip.size && skip.has(localPairKey(p.id, q.id))) continue
      let pos = top.length
      while (pos > 0 && rank(row, top[pos - 1]!) < 0) pos--
      top.splice(pos, 0, row)
      if (top.length > max) top.pop()
    }
  }
  return top.map(({ a, b, distance, dissimilarity }) => ({ a, b, distance, dissimilarity }))
}
